import type { RouterConfig } from '@nuxt/schema'
import type { RouteRecordRaw } from '#vue-router'
import makeLangRoutes from "~/utils/routes";
import {hasLengthAtLeast} from "~/utils/array";

// Portal CMS route aliases, ported from the nuxt2 `router.extendRoutes` block.
// The CMS publishes demonstrator content under several top-level portal
// prefixes; the catch-all prefixes are rendered by two CMS page components,
// and two specific paths carve out interactive pages of their own. Each alias
// maps a URL onto an already-ported page; PortalBase derives its content key
// from `route.path`, so the alias resolves to the right CMS document with no
// page-level changes.
//
// Paths are relative — these are attached as children of the file-based
// `/portal` route so they inherit its `role-guard` middleware (see
// app/pages/portal.vue). nuxt2 registered them as top-level records, which
// bypassed that guard and left non-demonstrators with a bare 404 from
// PortalBase instead of the redirect to /help/*. Nesting makes the aliases
// behave like the pages they alias.
//
// This completes the nuxt2 alias set — all six are registered.
//
// Order within the list is not load-bearing. vue-router 4 ranks matches by
// specificity, so a static `business-resources/current-updates` child beats the
// `business-resources/:pathMatch(.*)*` sibling regardless of declaration order —
// unlike nuxt2's vue-router 3, where array order decided it.
const PORTAL_ALIAS_PAGES = {
  'stampin-university': () => import('~/pages/portal/stampin-university/[...slug].vue'),
  'in-the-know': () => import('~/pages/portal/in-the-know/[...slug].vue'),
  'current-updates': () => import('~/pages/portal/current-updates.vue'),
  'dbws-subscribe-now': () => import('~/pages/portal/dbws/subscribe-now.vue')
}

const PORTAL_CMS_ALIASES: { name: string, path: string, page: keyof typeof PORTAL_ALIAS_PAGES }[] = [
  // Interactive page aliases — static, so they win over the catch-all sibling below.
  { name: 'portal-br-current-updates-alias', path: 'business-resources/current-updates', page: 'current-updates' },
  { name: 'portal-br-my-dbws-alias', path: 'business-resources/my-dbws', page: 'dbws-subscribe-now' },
  { name: 'portal-ordering-alias', path: 'ordering/:pathMatch(.*)*', page: 'stampin-university' },
  { name: 'portal-business-resources-alias', path: 'business-resources/:pathMatch(.*)*', page: 'stampin-university' },
  { name: 'portal-hall-of-fame-alias', path: 'hall-of-fame/:pathMatch(.*)*', page: 'in-the-know' },
  { name: 'portal-happening-now-alias', path: 'happening-now/:pathMatch(.*)*', page: 'in-the-know' }
]

/**
 * Attach the portal CMS aliases as children of the file-based `/portal` route.
 *
 * Falls back to absolute top-level records if that parent is ever missing, so a
 * change in how Nuxt nests `portal.vue` + `pages/portal/*` degrades to nuxt2's
 * behaviour (routes resolve, guard comes from PortalBase) rather than silently
 * dropping the aliases.
 */
export const addPortalCmsAliases = (routes: readonly RouteRecordRaw[]): RouteRecordRaw[] => {
  const aliasRoutes = PORTAL_CMS_ALIASES.map(alias => ({
    name: alias.name,
    path: alias.path,
    component: PORTAL_ALIAS_PAGES[alias.page]
  })) as RouteRecordRaw[]

  const portalIndex = routes.findIndex(route => route.path === '/portal' && Array.isArray(route.children))
  if (portalIndex === -1) {
    return [...routes, ...aliasRoutes.map(route => ({ ...route, path: `/portal/${route.path}` }))]
  }

  return routes.map((route, index) => index === portalIndex
    ? { ...route, children: [...(route.children ?? []), ...aliasRoutes] } as RouteRecordRaw
    : route)
}
const CMS_CONTENT_PAGE = () => import('~/components/CmsContentPage.vue')

/**
 * Register the public CMS routes that do not have file-based pages.
 *
 * `/sparkit` is not one of them: the landing page has its own layout and scroll
 * behaviour, so it lives in `app/pages/sparkit/index.vue` as it did in nuxt2.
 */
export const addPublicCmsRoutes = (routes: readonly RouteRecordRaw[]): RouteRecordRaw[] => [
  ...routes,
  {
    name: 'help',
    path: '/help/:slug',
    alias: ['/company/:slug', '/community/:slug', '/legal/:slug', '/info/:slug'],
    component: CMS_CONTENT_PAGE
  }
]


export default {
   // scrollBehavior(to, from, savedPosition) {
   //     let position = {left: 0, top: 0}
   //
   //     if (to.params.noScroll) {
   //         position = {
   //             left: window.pageXOffset,
   //             top: window.pageYOffset
   //         }
   //     }
   //     // savedPosition is only available for popstate navigations (back button)
   //     if (savedPosition) {
   //         position = savedPosition
   //     }
   //
   //     if (to.path !== from.path) {
   //         nextTick(() => useEvent('triggerScroll'))
   //     }
   //
   //     return new Promise((resolve) => {
   //         // wait for the out transition to complete (if necessary)
   //         useListen('triggerScroll', () => {
   //             // coords will be used if no selector is provided,
   //             // or if the selector didn't match any element.
   //             if (to.hash) {
   //                 let hash = to.hash
   //                 // CSS.escape() is not supported with IE and Edge.
   //                 if (typeof window.CSS !== 'undefined' && typeof window.CSS.escape !== 'undefined') {
   //                     hash = '#' + window.CSS.escape(hash.substr(1))
   //                 }
   //                 try {
   //                     if (document.querySelector(hash)) {
   //                         // scroll to anchor by returning the selector
   //                         position = {selector: hash}
   //                     }
   //                 } catch (e) {
   //                     // eslint-disable-next-line no-console
   //                     console.warn('Failed to save scroll position. Please add CSS.escape() polyfill (https://github.com/mathiasbynens/CSS.escape).')
   //                 }
   //             }
   //             resolve(position)
   //         })
   //     })
   // },
  // https://router.vuejs.org/api/interfaces/routeroptions.html#routes
  routes: (routes) => {
    const urlSegments = import.meta.server
      ? useNuxtApp().ssrContext?.event.node.req.url?.split('/') ?? []
      : window.location.pathname.split('/')
    const lang = hasLengthAtLeast(urlSegments, 2) && /^[^?].($|\?)/.test(urlSegments[1])
      ? urlSegments[1].slice(0, 2)
      : null

    const newRoutes = addPublicCmsRoutes(addPortalCmsAliases(routes))
    if (lang) {
      return makeLangRoutes(newRoutes, lang)
    }
    return newRoutes
  },
} satisfies RouterConfig
